import { nextTick } from "vue";
import { cloneDeep, randomId, waitForMe } from "@/shared/utils/helpers.js";
import { copy, parseHtml } from "@/shared/utils/string.js";

import { footersService } from "@/apps/legal/services/footers.service.js";
import { footerUploadService } from "@/apps/legal/services/footer-upload.service.js";
import { iframeMutations } from "@/shared/store/iframe.mutations";
import { legalSnapshotService } from "@/apps/legal/store/legal-snapshot.service.js";
import { shareUrlService } from "@/shared/projects/share-url.service";
import { FooterIcon, NewFileIcon } from "@easy/ui/icons";
import { markRaw } from "vue";
import { t } from "@/i18n";
import { projectsService } from "@/shared/projects/projects.service";

export default {
    namespaced: true,
    state: () => ({
        footer: {},
        legal: {},
        categories: [],
        companyDataGhost: {},
        iframe: null,
        iframeLoaded: false,
        hasChanges: false,
        hasSnapshot: false,
    }),
    getters: {
        appUrl: (s, g, rootS, rootGetters) => rootGetters.easyUrls.legal,
        footerLink: (state, g, rootState) => `${rootState.globalUrl}/footers/${state.footer.uuid}`,
        termsLink: (state, g, rootState) => `${rootState.globalUrl}/terms/${state.legal.uuid}`,
        policyLink: (state, g, rootState) => `${rootState.globalUrl}/policies/${state.legal.uuid}`,
        aboutLink: (state, g, rootState) => `${rootState.globalUrl}/about/${state.legal.uuid}`,
        iframeCode: (state, g, rootState) => (view, uuid, path) => {
            const id = `${view === "footer" ? "" : view + "_"}${uuid}`;
            return `<iframe
                id="${id}"
                width="100%"
                src="${rootState.globalUrl}/${path}/${uuid}"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                frameborder="0"
                scrolling="no"
                ></iframe>
                <script> window.addEventListener("message", (e) => {!!e.data.frameHeight && e.data.footerUuid === "${state.footer.uuid}" && (document.getElementById("${id}").style.height = e.data.frameHeight);});</script>`;
        },
        footerIframe: (state, getters) =>
            getters.iframeCode("footer", state.footer.uuid, "footers"),
        termsIframe: (state, getters) =>
            getters.iframeCode("terms", state.legal.uuid, "terms-embed"),
        policyIframe: (state, getters) =>
            getters.iframeCode("policies", state.legal.uuid, "policies-embed"),
    },
    mutations: {
        ...iframeMutations,
        changeLink(state, payload) {
            const updatedLink = Object.assign({}, payload);

            const category = state.categories.find((x) => x.id === payload.categoryId);
            const index = category.links.map((x) => x.id).indexOf(payload.id);
            if (index > -1) {
                category.links.splice(index, 1, updatedLink);
            }
        },
        changeCategory(state, payload) {
            const updatedCategory = Object.assign({}, payload);

            const index = state.categories.map((x) => x.id).indexOf(payload.id);
            if (index > -1) {
                state.categories.splice(index, 1, updatedCategory);
            }
        },
        setLinks(state) {
            state.categories.forEach((category) => {
                category.links
                    .filter((l) => !l._deleted)
                    .forEach((link, index) => {
                        link.categoryId = category.id;
                        link.order = index;
                        if (!link._created) {
                            link["_changed"] = true;
                        }
                    });
            });
        },
        setFooter(state, payload) {
            state.footer = cloneDeep(payload.footer);
        },
        setProject(state, { footer, legal, snapshot = {} }) {
            state.footer = legalSnapshotService.makeSnapshotState(
                state,
                "footer",
                footer,
                snapshot
            );
            state.legal = legalSnapshotService.makeSnapshotState(state, "legal", legal, snapshot);
            state.categories = legalSnapshotService.makeSnapshotState(
                state,
                "categories",
                footer.categories,
                snapshot
            );
            state.footer.companyData = legalSnapshotService.makeSnapshotState(
                state,
                "companyDataGhost",
                state.footer.companyData || {},
                snapshot
            );
            state.hasChanges = false;
        },
        clearProject(state) {
            state.footer = {};
            state.legal = {};
            state.categories = [];
            legalSnapshotService.clearSnapshot(state, "footer", {});
            legalSnapshotService.clearSnapshot(state, "legal", {});
            legalSnapshotService.clearSnapshot(state, "categories", []);
            legalSnapshotService.clearSnapshot(state, "companyDataGhost", {});
        },
        addLink(state, link) {
            const category = state.categories.find((x) => x.id === link.categoryId);
            if (category) {
                link["_created"] = true;
                link.id = randomId();
                link.order = category.links.length;
                category.links.push(link);
            }
        },
        addCategory(state) {
            state.categories.push({
                _created: true,
                id: randomId(),
                footerId: state.footer.id,
                name: "",
                links: [],
            });
        },

        removeCategory(state, category) {
            if (category._created) {
                const index = state.categories.indexOf(category);
                if (index > -1) {
                    state.categories.splice(index, 1);
                }
            } else {
                delete category._changed;
                category["_deleted"] = true;
                category.links.forEach((link, index) => {
                    if (link._created) {
                        category.links.splice(index, 1);
                    } else {
                        delete link._changed;
                        link["_deleted"] = true;
                    }
                });
            }
        },
    },
    actions: {
        refreshIframe({ rootState, state, getters }) {
            const footer = cloneDeep(state.footer);
            footer.legal = cloneDeep(state.legal);
            footer.categories = cloneDeep(state.categories);
            footer.nda = cloneDeep(rootState["legal/ndas"].nda);
            state.iframe = document.querySelector("#iframe");
            if (state.iframe && footer.id) {
                state.iframe.contentWindow.postMessage({ footerData: footer }, getters.appUrl);
            }
        },
        cloneLink({ commit }, link) {
            const clonedLink = cloneDeep(link);
            commit("addLink", clonedLink);
        },
        changeIframeView({ state, rootState, getters }) {
            state.iframe = document.querySelector("#iframe");
            if (state.iframe && state.footer.id) {
                state.iframe.contentWindow.postMessage(
                    { footerView: rootState.view },
                    getters.appUrl
                );
            }
        },
        async readLogoFile({ state, rootState }, payload) {
            rootState.waiting = true;
            try {
                const { logo } = await footerUploadService.footerLogo({
                    file: payload.file,
                    footerId: state.footer.id,
                });

                state.footer.logo = logo;
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        deleteLink({ state, commit }, link) {
            if (link._created) {
                const category = state.categories.find((c) => c.id == link.categoryId);
                const index = category.links.indexOf(link);
                if (index > -1) {
                    category.links.splice(index, 1);
                }
            } else {
                delete link._changed;
                link["_deleted"] = true;
            }
            commit("setLinks");
        },
        toggleCategories({ state, commit }) {
            state.categories.forEach((cat, index) => {
                if (index !== 0) {
                    state.categories[0].links.push(...cat.links);
                    cat.links = [];
                }
            });
            state.footer.withCategories = !state.footer.withCategories;
            commit("setLinks");
        },
        showLinkModal({ state, rootState, getters, commit }) {
            const footerSlug = state.footer.uuid;
            const footerURL = shareUrlService.makeUrlWithLang(
                "footers",
                footerSlug,
                rootState.previewLang
            );
            const legalSlug = state.legal.uuid;
            const termsURL = shareUrlService.makeUrlWithLang(
                "terms",
                legalSlug,
                rootState.previewLang
            );
            const privacyURL = shareUrlService.makeUrlWithLang(
                "policies",
                legalSlug,
                rootState.previewLang
            );
            const aboutURL = shareUrlService.makeUrlWithLang(
                "about",
                legalSlug,
                rootState.previewLang
            );
            const modalData = {
                type: "link",
                title: t("legal.share_modal.title"),
                links: [
                    {
                        value: "footer",
                        label: t("legal.views.footer"),
                        url: footerURL,
                        icon: markRaw(FooterIcon),
                        code: getters.footerIframe,
                    },
                    {
                        value: "terms",
                        label: t("legal.views.terms"),
                        url: termsURL,
                        icon: markRaw(NewFileIcon),
                        code: getters.termsIframe,
                    },
                    {
                        value: "privacy",
                        label: t("legal.views.privacy"),
                        url: markRaw(privacyURL),
                        icon: markRaw(NewFileIcon),
                        code: getters.policyIframe,
                    },
                    {
                        value: "about",
                        label: t("legal.views.about"),
                        url: aboutURL,
                        icon: markRaw(NewFileIcon),
                        code: "",
                    },
                ],
                code: getters.footerIframe,
                actionBtn: t("legal.share_modal.copy_code"),
                cancelBtn: t("shared.close"),
                callback: () => {
                    copy(getters.footerIframe);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("shared.code_copied"),
                        },
                        { root: true }
                    );
                },
            };
            commit("showModal", modalData, { root: true });
        },
        publishProject({ rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            dispatch("hostFooter");
        },
        async unpublishFooter({ state, commit, dispatch }) {
            await dispatch("unpublish", { uuid: state.footer.uuid });
            commit("setUnpublished", state.footer, { root: true });
            dispatch("legal/projects/mergeSnapshotChanges", null, { root: true });
        },
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        async unpublish({ _ }, { uuid }) {
            return projectsService.publish({ tool: "legal", uuid, publish: false });
        },
        async hostFooter({ state, rootState, commit, rootGetters, dispatch }) {
            rootState.waiting = true;
            const firstPublish = !rootGetters.isPublished(state.footer);

            await waitForMe(100);
            await nextTick();

            try {
                await projectsService.publish({
                    tool: "legal",
                    uuid: state.footer.uuid,
                    publish: true,
                });

                commit("setPublished", state.footer, { root: true });
                dispatch("legal/projects/mergeSnapshotChanges", null, { root: true });
                if (firstPublish) {
                    dispatch("showLinkModal");
                }
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        showCodeFooter({ state, rootState, commit, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }
            rootState.view = "footer";
            setTimeout(() => {
                nextTick(() => {
                    const code = `<div>
                ${parseHtml(state.iframe.contentDocument.head.innerHTML)}
                ${parseHtml(state.iframe.contentDocument.body.innerHTML)}
            </div>`;
                    const modalData = {
                        type: "code",
                        title: t("shared.your_code"),
                        code: code,
                        actionBtn: t("shared.copy_code"),
                        cancelBtn: t("legal.share_modal.download_into_html"),
                        callback: () => {
                            copy(code);
                            commit(
                                "showAlert",
                                {
                                    type: "success",
                                    title: t("shared.code_copied"),
                                },
                                { root: true }
                            );
                        },
                        cancelCallback: () => {
                            dispatch("downloadFooter");
                        },
                    };
                    commit("showModal", modalData, { root: true });
                });
            }, 100);
        },
        downloadFooter({ state, rootState, dispatch }) {
            rootState.view = "footer";
            setTimeout(() => {
                dispatch("download", {
                    data: parseHtml(state.iframe.contentDocument.documentElement.innerHTML),
                    filename: "footer.html",
                    type: "text/html",
                });
            }, 1000);
        },
        async getCompanyData({ state, rootState }, nip) {
            rootState.waiting = true;
            try {
                const data = await footersService.getCompanyData(nip);
                state.legal.fullData = `${data.name}\n${data.firstName} ${data.lastName}\n${
                    data.street
                } ${data.streetNumber}${data.houseNumber ? "/" + data.houseNumber : ""}\n${
                    data.city
                }\n${data.postCode} ${data.postCity} `;
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        changeFontFamily({ state }, payload) {
            state.footer.fontFamily = payload;
        },
    },
};
